import { motion } from "framer-motion";
import PageHero from "@/components/site/PageHero";
import { Reveal, ScaleImage } from "@/components/site/Reveal";
import { TimelineCarousel } from "@/components/site/TimelineCarousel";
import { COMPANY, MISSION, DRIVERS, CERTIFICATIONS } from "@/data/content";

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        index="/ 02"
        overline="About Us"
        title={<>Three decades of<br />precision, delivered.</>}
        subtitle={COMPANY.positioning}
      />

      {/* STORY */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1500px] px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-7">
            <Reveal>
              <span className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
                [ The Company ]
              </span>
              <p className="mt-8 font-display font-medium text-2xl md:text-3xl leading-[1.35] tracking-tight">
                {COMPANY.overview}
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <ScaleImage
              src="/assets/images/workshop/about_us_warehouse_interior.jpg"
              alt="Krystal Technology facility interior"
              className="h-[320px] lg:h-[400px]"
            />
          </div>
        </div>
      </section>

      {/* CEO'S NOTE */}
      <section className="py-16 md:py-24 border-t border-border bg-secondary/40" data-testid="ceo-note-section">
        <div className="mx-auto max-w-[1500px] px-6 md:px-12">
          <Reveal>
            <span className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
              [ CEO's Message ]
            </span>
            <h2 className="mt-6 mb-14 font-display font-medium tracking-tight text-4xl md:text-6xl leading-[1.02]">
              Built Once. Trusted for Decades.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            <div className="lg:col-span-4">
              <Reveal>
                <img
                  src="/assets/team/ceo.jpeg"
                  alt="Tanmay Panchal — CEO, Krystal Technology"
                  data-testid="ceo-photo"
                  className="w-full max-w-xs lg:max-w-none aspect-[4/5] object-cover object-top grayscale"
                />
              </Reveal>
            </div>
            <div className="lg:col-span-8">
              <Reveal delay={0.1}>
                <div className="space-y-5 text-base md:text-lg text-muted-foreground leading-relaxed max-w-3xl">
                  <p>
                    When we began in 1993, the goal was never to be the biggest. The goal was to be
                    trusted. In manufacturing, trust is earned the hard way — through consistency, accountability,
                    precision, and the discipline to get every detail right.
                  </p>
                  <p>
                    Today, our manufacturing capabilities serve customers across continents, supporting industries
                    that cannot afford failure — marine, dredging, offshore, steel and more. We have grown from a
                    small shopfloor to a global manufacturing partner, but one principle has never changed:
                  </p>
                  <p className="text-foreground font-display font-medium text-xl md:text-2xl tracking-tight leading-snug border-l-2 border-[hsl(var(--signal))] pl-5">
                    If it carries our name, it must reflect the precision and reliability we stand for.
                  </p>
                  <p>
                    We continue to invest in advanced technology, engineering expertise, and our people — building the
                    foundation to take on greater challenges.
                  </p>
                  <p>
                    To every client who has trusted us with their projects, we do not take that trust lightly. And to
                    those just beginning their journey with us — we look forward to proving it.
                  </p>
                </div>
                <div className="mt-10 pt-6 border-t border-border">
                  <p className="font-display font-medium text-lg tracking-tight text-foreground">Tanmay Panchal</p>
                  <p className="font-mono-tech text-[11px] uppercase tracking-[0.25em] text-muted-foreground mt-1">
                    CEO, Krystal Technology
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* CERTIFICATIONS & STANDARDS */}
      <section className="py-16 md:py-24 border-t border-border" data-testid="certifications-section">
        <div className="mx-auto max-w-[1500px] px-6 md:px-12">
          <Reveal>
            <span className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
              [ Certifications & Standards ]
            </span>
            <h2 className="mt-4 mb-16 font-display font-medium tracking-tight text-4xl md:text-6xl leading-[1] max-w-2xl">
              Certified quality
            </h2>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-12" data-testid="certifications-grid">
            {CERTIFICATIONS.map((c, i) => (
              <Reveal key={c.label} delay={(i % 5) * 0.06}>
                <div className="flex flex-col items-center text-center" data-testid={`cert-item-${i}`}>
                  <div className="h-[60px] flex items-center justify-center">
                    <img src={c.logo} alt={c.label} className="h-[60px] w-auto object-contain" />
                  </div>
                  <p className="mt-5 font-display font-medium text-lg tracking-tight">{c.label}</p>
                  <p className="text-muted-foreground text-sm mt-2 leading-relaxed max-w-[230px]">{c.detail}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-20">
              <p className="font-mono-tech text-sm text-[hsl(var(--signal))] mb-10">// QUALITY OBJECTIVES</p>
              <div className="grid grid-cols-1 md:grid-cols-2 border-t border-l border-border">
                {MISSION.objectives.slice(0, 4).map((o, i) => (
                  <div key={i} className="flex gap-5 border-r border-b border-border p-8">
                    <span className="font-mono-tech text-sm text-muted-foreground">0{i + 1}</span>
                    <p className="text-muted-foreground leading-relaxed">{o}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHAT DRIVES US */}
      <section className="py-16 md:py-24 border-t border-border" data-testid="what-drives-us">
        <div className="mx-auto max-w-[1500px] px-6 md:px-12">
          <Reveal>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-16">
              <div className="lg:col-span-7">
                <span className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
                  [ What Drives Us ]
                </span>
                <h2 className="mt-4 font-display font-medium tracking-tight text-4xl md:text-6xl leading-[1] max-w-2xl">
                  The values behind every component.
                </h2>
              </div>
              <p className="lg:col-span-5 self-end text-muted-foreground leading-relaxed">
                Our work is defined by the standards we refuse to lower — the principles that guide how we engineer, inspect and deliver.
              </p>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 border-t border-l border-border">
            {DRIVERS.map((d, i) => (
              <Reveal key={d.no} delay={(i % 2) * 0.08}>
                <div className="group h-full border-r border-b border-border p-8 md:p-10 hover:bg-secondary transition-colors">
                  <p className="font-mono-tech text-sm text-[hsl(var(--signal))] mb-8">{d.no}</p>
                  <h3 className="font-display font-medium text-2xl md:text-3xl tracking-tight mb-4">{d.title}</h3>
                  <p className="text-muted-foreground leading-relaxed max-w-md">{d.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MISSION / VISION / OBJECTIVES */}
      <section className="py-16 md:py-24 bg-[#0A0A0A] text-white">
        <div className="mx-auto max-w-[1500px] px-6 md:px-12">
          <Reveal>
            <span className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-white/40">
              [ Manifesto ]
            </span>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-y-16 gap-x-20">
            <Reveal>
              <div>
                <p className="font-mono-tech text-sm text-[hsl(var(--signal))] mb-6">01 // MISSION</p>
                <p className="font-display font-medium text-3xl md:text-4xl leading-[1.2] tracking-tight">
                  {MISSION.mission}
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div>
                <p className="font-mono-tech text-sm text-[hsl(var(--signal))] mb-6">02 // VISION</p>
                <p className="font-display font-medium text-3xl md:text-4xl leading-[1.2] tracking-tight">
                  {MISSION.vision}
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="py-16 md:py-24 bg-secondary">
        <div className="mx-auto max-w-[1500px] px-6 md:px-12">
          <Reveal>
            <span className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
              [ Legacy — A Timeline ]
            </span>
            <h2 className="mt-4 mb-16 font-display font-medium tracking-tight text-4xl md:text-6xl leading-[1] max-w-4xl">
              From 1993 to a Global Future
            </h2>
          </Reveal>

          <TimelineCarousel />
        </div>
      </section>
    </div>
  );
}
